// @givmo/app-charity — the pre-approval Charity Terms read's decode (CPA-WEB12).
//
// ★ THIS DECODE FAILS CLOSED, AND THAT IS THE POINT — it is the one tolerant reader in this app that
// answers `null` rather than a usable fallback. Every other decode here reads a LISTING and falls back
// to `[]`, because an empty table is an honest thing to show. This one reads a legal instrument whose
// `version_number` and `content_hash` are the values the claim submit ECHOES into a §318(a) acceptance
// record. A decode that let a missing hash through as `undefined` would not produce a broken screen —
// it would produce a recorded consent pinned to nothing, which is precisely the state D-CP-8 exists to
// make impossible. So: all three load-bearing fields present and well-formed, or no document at all and
// the screen says so.
//
// The generated `CharityTermsOut` is the compile-time contract (it exists in the snapshot — the
// membership-gated sibling read returns it); this module validates the bytes that actually arrived,
// because a generated type is a claim about the wire derived from a committed snapshot, not proof.

import type { CharityTermsOut } from '@givmo/api-client';

/** Whether the value is a plain object we can read keys off. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * The served Charity Terms, or `null` if what arrived cannot pin an acceptance.
 *
 * Required, no fallback:
 *   - `version_number` — a finite integer. The acceptance row's version column; `0` is legitimate
 *     arithmetically but no version is ever published as 0, so it is accepted here rather than
 *     special-cased: this decode's job is shape, and the backend's own drift guard is the authority on
 *     which version is current.
 *   - `content_hash` — a non-empty string. The pin between the bytes shown and the row written.
 *   - `content_markdown` — a non-empty string. An empty body would render an empty agreement with a
 *     live Accept control under it, which is the worst reachable state on this screen.
 *
 * Optional, nulled:
 *   - `display_label` — the signed instrument's own label ("1.1"). Absent ⇒ the caller falls back to
 *     `version_number` (see {@link termsVersionLabel}).
 *   - `effective_at` — an ISO-8601 instant. Absent or unparseable is rendered as such, never guessed.
 *   - `organization_name` — the organization as the body names it, the catalog name, never a display
 *     name (CPA-BE82): the name the two affirmations and the submit's `attested_legal_name` carry.
 *     Absent (a backend before CPA-BE82) ⇒ the caller falls back to the name it read before.
 *
 * ⚠ The api-client's response middleware unwraps a `{data,meta,links}` envelope before this sees it,
 * and this endpoint serves a BARE object (a singleton, no envelope). Both shapes are read anyway: a
 * singleton wrapped by a future backend change would otherwise decode to `null` and dead-end the
 * wizard, and reading one extra key is cheaper than that failure mode.
 */
export function decodeClaimTerms(body: unknown): CharityTermsOut | null {
  if (!isRecord(body)) return null;
  const source = isRecord(body.data) ? body.data : body;

  const versionNumber = source.version_number;
  if (typeof versionNumber !== 'number' || !Number.isFinite(versionNumber)) return null;

  const contentHash = optionalString(source.content_hash);
  if (contentHash === null) return null;

  const contentMarkdown = source.content_markdown;
  if (typeof contentMarkdown !== 'string' || contentMarkdown.length === 0) return null;

  return {
    version_number: versionNumber,
    display_label: optionalString(source.display_label),
    content_hash: contentHash,
    content_markdown: contentMarkdown,
    effective_at: optionalString(source.effective_at),
    organization_name: optionalString(source.organization_name),
  };
}

/**
 * How this version is NAMED to a reader: the signed instrument's own label, or the wire's version
 * number when it carries none.
 *
 * The label is what the President signed the document as ("1.1"); `version_number` is the database's
 * ordinal (2). They are different facts, and the reader's audit anchor is the former — so a surface
 * that showed the ordinal would be naming the document by something that appears nowhere on it.
 */
export function termsVersionLabel(terms: CharityTermsOut): string {
  return terms.display_label ?? String(terms.version_number);
}
